import { useNavigate } from "react-router-dom";
import MaintenanceImg from "../../../assets/images/maintenance.webp";
import { useEffect, useState, useRef } from "react";
import { isSiteUnderMaintenance } from "./Maintenance.hook";
import {
  setMaintenanceTimer,
  useAppDispatch,
  useAppSelector,
  LOGIN_ROUTE,
} from "@hypexone/uicore";
import { BiLoaderCircle } from "react-icons/bi";

type TimeLeft = { hours: string; minutes: string; seconds: string } | null;

const Maintenance = () => {
  const { maintenanceTime } = useAppSelector((state) => state.catalog);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const endTimeRef = useRef<number | null>(null);

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(null);

  const checkSiteUnderMaintenance = async () => {
    try {
      const res = await isSiteUnderMaintenance();
      if (!res?.isSiteUnderMaintenance) {
        navigate(LOGIN_ROUTE, { replace: true });
      } else {
        sessionStorage.removeItem("aid");
        sessionStorage.removeItem("jwt_token");
        sessionStorage.removeItem("username");
        document.cookie =
          "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        dispatch(setMaintenanceTimer(res.downtimeTimestamp));
      }
    } catch (error) {
      console.error("Error checking site maintenance:", error);
    }
  };

  useEffect(() => {
    checkSiteUnderMaintenance();
  }, []);

  useEffect(() => {
    if (!maintenanceTime) {
      setTimeLeft(null);
      return;
    }
    const endTime = new Date(maintenanceTime).getTime();
    endTimeRef.current = endTime;

    const updateTimeLeft = () => {
      if (endTimeRef.current === null) return;
      const difference = endTimeRef.current - Date.now();
      if (difference <= 0) {
        setTimeLeft({ hours: "00", minutes: "00", seconds: "00" });
        return;
      }
      setTimeLeft({
        hours: String(Math.floor(difference / (1000 * 60 * 60))).padStart(
          2,
          "0"
        ),
        minutes: String(
          Math.floor((difference / (1000 * 60)) % 60)
        ).padStart(2, "0"),
        seconds: String(Math.floor((difference / 1000) % 60)).padStart(
          2,
          "0"
        ),
      });
    };

    updateTimeLeft();
    const timer = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [maintenanceTime]);

  return (
    <div className="h-full w-full bg-[#071123] flex justify-center align-middle">
      <div className="text-[#F2F6FF] flex flex-col justify-center items-center align-middle text-center">
        <img src={MaintenanceImg} className="w-[280px] md:w-[400px]" alt="" />
        <div className="text-xl mt-4 md:text-6xl font-bold md:mt-10">
          The site is under maintenance
        </div>
        <div className="text-xs mt-2.5 w-[90%] opacity-80 md:text-xl font-normal md:mt-6 md:w-[75%]">
          Our website is currently undergoing maintenance to improve your
          experience. We appreciate your patience and will be back online
          shortly.
        </div>

        {timeLeft !== null ? (
          <div className="mt-8 flex gap-4 items-center">
            <div className="text-center">
              <div className="text-4xl">{timeLeft.hours}</div>
              <div className="text-xs opacity-60 pt-1">Hours</div>
            </div>
            <div className="h-6 bg-white opacity-30 w-[1.5px]"></div>
            <div className="text-center">
              <div className="text-4xl">{timeLeft.minutes}</div>
              <div className="text-xs opacity-60 pt-1">Minutes</div>
            </div>
            <div className="h-6 bg-white opacity-30 w-[1.5px]"></div>
            <div className="text-center">
              <div className="text-4xl">{timeLeft.seconds}</div>
              <div className="text-xs opacity-60 pt-1">Seconds</div>
            </div>
          </div>
        ) : (
          <div className="mt-8 flex gap-4 items-center">
            <BiLoaderCircle className="animate-spin inset-0 m-auto h-16 w-16 z-50 " />
          </div>
        )}
      </div>
    </div>
  );
};

export default Maintenance;
